import React, { useEffect, useState } from 'react';
import { CalendarDays, ChevronDown, TrendingUp, TrendingDown, Minus, Play, RotateCcw } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { Calendar } from '../../ui/calendar';
import { Button } from '../../ui/button';
import { useCountUp, Sparkline } from '../../shared/Motion';
import { BreakStatusPill, BreakAvatar, BreakEmptyState, BreakLoadingState, BreakErrorState, BreakPagination, BreakTh } from '../../hr/break/ui/BreakUi';

// ANALYTICS design system — presentation only, shared by every Analytics tab. Typography = TitleFlow standard.
// Status pills / avatars / empty / loading / error / pagination / sortable th are the same primitives Break Management uses.
export { BreakStatusPill as AnStatusPill, BreakAvatar as AnAvatar, BreakEmptyState as AnEmptyState, BreakLoadingState as AnLoadingState, BreakErrorState as AnErrorState, BreakPagination as AnPagination, BreakTh as AnTh };

export const AnCard = ({ children, className = '', testId, hover = false, delay = 0, style }) => (
  <div className={`an-rise bg-white rounded-2xl border border-slate-200/80 shadow-[0_1px_2px_rgba(15,23,42,.04),0_8px_24px_-14px_rgba(15,23,42,.14)] ${hover ? 'an-hover' : ''} ${className}`} style={{ animationDelay: `${delay}ms`, ...style }} data-testid={testId}>{children}</div>
);

export const AnPageHeader = ({ icon: Icon, title, desc, right, testId = 'analytics-page-header' }) => (
  <div className="flex items-center gap-3 min-w-0" data-testid={testId}>
    <div className="h-11 w-11 shrink-0 rounded-xl bg-gradient-to-br from-blue-50 to-violet-50 text-blue-600 flex items-center justify-center border border-blue-100/70 shadow-sm"><Icon className="h-5 w-5" /></div>
    <div className="min-w-0">
      <h1 className="text-[22px] font-bold text-slate-900 tracking-tight leading-tight">{title}</h1>
      <p className="text-[12.5px] text-slate-500 leading-snug">{desc}</p>
    </div>
    {right}
  </div>
);

/** Filter bar: white card, fields with icon tile + small label + control. `actions` right-aligned. */
export const AnFilterBar = ({ children, actions, testId, className = '' }) => (
  <AnCard className={`px-3 py-2.5 flex flex-wrap items-center gap-2 ${className}`} testId={testId}>
    {children}
    {actions && <div className="ml-auto flex items-center gap-2 flex-wrap">{actions}</div>}
  </AnCard>
);

export const AnFilterField = ({ icon: Icon, label, children, className = '', testId }) => (
  <div className={`flex items-center gap-2.5 rounded-xl border border-slate-200 bg-white h-11 pl-2 pr-1.5 min-w-0 ${className}`} data-testid={testId}>
    {Icon && <span className="h-7 w-7 shrink-0 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center"><Icon className="h-3.5 w-3.5" /></span>}
    <div className="min-w-0 flex-1">
      <div className="text-[10.5px] leading-none text-slate-400 font-medium mb-0.5 whitespace-nowrap">{label}</div>
      <div className="an-field-control">{children}</div>
    </div>
  </div>
);

const fmtRange = (iso) => { if (!iso) return null; const [y, m, d] = iso.split('-').map(Number); return new Date(y, m - 1, d).toLocaleString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }); };
const toIso = (d) => d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : '';
const fromIso = (iso) => { if (!iso) return undefined; const [y, m, d] = iso.split('-').map(Number); return new Date(y, m - 1, d); };

/** Combined From/To control. Presentation only: emits the same ISO yyyy-mm-dd strings the page already used. */
export const AnDateRange = ({ start, end, onChange, placeholder = 'All dates', max, testId = 'an-date-range', className = '' }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ from: fromIso(start), to: fromIso(end) });
  useEffect(() => { if (open) setDraft({ from: fromIso(start), to: fromIso(end) }); }, [open, start, end]);
  const label = start || end ? `${fmtRange(start) || '…'} – ${fmtRange(end) || '…'}` : placeholder;
  const apply = () => { onChange({ start: toIso(draft.from), end: toIso(draft.to || draft.from) }); setOpen(false); };
  const today = new Date();
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={`flex items-center gap-2.5 rounded-xl border border-slate-200 bg-white h-11 pl-2 pr-2.5 text-left an-btn hover:border-blue-300 hover:shadow-sm ${className}`} data-testid={testId} aria-expanded={open}>
          <span className="h-7 w-7 shrink-0 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center"><CalendarDays className="h-3.5 w-3.5" /></span>
          <span className="min-w-0">
            <span className="block text-[10.5px] leading-none text-slate-400 font-medium mb-0.5">Date Range</span>
            <span className="block text-[13px] font-semibold text-slate-800 leading-tight whitespace-nowrap" data-testid={`${testId}-label`}>{label}</span>
          </span>
          <ChevronDown className={`h-4 w-4 text-slate-400 ml-1 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0 rounded-2xl border-slate-200 shadow-[0_16px_40px_-12px_rgba(37,99,235,.28)] overflow-hidden an-pop" data-testid={`${testId}-popover`}>
        <div className="px-3 pt-2.5 pb-1 flex items-center justify-between text-[11px] text-slate-500">
          <span>{draft.from ? fmtRange(toIso(draft.from)) : 'Start date'} <span className="text-slate-300">→</span> {draft.to ? fmtRange(toIso(draft.to)) : 'End date'}</span>
          <button type="button" className="font-semibold text-blue-600 hover:underline" onClick={() => setDraft({ from: today, to: today })} data-testid={`${testId}-today`}>Today</button>
        </div>
        <Calendar mode="range" numberOfMonths={2} selected={draft} onSelect={(r) => setDraft(r || {})} defaultMonth={draft.from || today} toDate={max ? fromIso(max) : undefined} className="an-calendar p-2" />
        <div className="flex items-center justify-end gap-2 px-3 py-2 border-t border-slate-100 bg-slate-50/60">
          <Button variant="ghost" size="sm" className="h-8 text-[12.5px]" onClick={() => setOpen(false)} data-testid={`${testId}-cancel`}>Cancel</Button>
          <Button size="sm" className="h-8 text-[12.5px] an-btn-primary" onClick={apply} disabled={!draft.from} data-testid={`${testId}-apply`}>Apply</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const AnResetButton = ({ onClick, testId = 'an-reset', children = 'Reset', className = '' }) => (
  <Button variant="outline" onClick={onClick} className={`an-btn h-11 px-4 rounded-xl text-[13px] font-semibold text-slate-700 ${className}`} data-testid={testId}><RotateCcw className="h-4 w-4 mr-1.5" />{children}</Button>
);
export const AnRunButton = ({ onClick, loading, testId = 'an-run', children = 'Run Report', icon: Icon = Play, className = '', disabled }) => (
  <Button onClick={onClick} disabled={disabled || loading} className={`an-btn an-btn-primary h-11 px-5 rounded-xl text-[13px] font-semibold ${className}`} data-testid={testId}>
    <Icon className={`h-4 w-4 mr-1.5 ${loading ? 'animate-pulse' : ''}`} />{children}
  </Button>
);

const TONES = {
  green: { tile: 'bg-emerald-50 text-emerald-600', line: '#10b981', bg: 'from-emerald-50/70' },
  red: { tile: 'bg-rose-50 text-rose-500', line: '#ef4444', bg: 'from-rose-50/70' },
  blue: { tile: 'bg-blue-50 text-blue-600', line: '#2563eb', bg: 'from-blue-50/70' },
  sky: { tile: 'bg-sky-50 text-sky-600', line: '#0ea5e9', bg: 'from-sky-50/70' },
  purple: { tile: 'bg-violet-50 text-violet-600', line: '#7c3aed', bg: 'from-violet-50/70' },
  amber: { tile: 'bg-amber-50 text-amber-600', line: '#d97706', bg: 'from-amber-50/70' },
  orange: { tile: 'bg-orange-50 text-orange-600', line: '#f97316', bg: 'from-orange-50/70' },
  cyan: { tile: 'bg-cyan-50 text-cyan-600', line: '#06b6d4', bg: 'from-cyan-50/70' },
  slate: { tile: 'bg-slate-100 text-slate-600', line: '#64748b', bg: 'from-slate-50' },
};

/** Delta chip — only rendered when a real comparison exists (pct null → neutral "—"). */
export const AnDelta = ({ pct, invert = false, suffix = '%', label, testId, abs }) => {
  if (pct === undefined) return null;
  const flat = pct === 0 || pct == null; const up = pct > 0; const good = flat ? null : (invert ? !up : up);
  const cls = flat ? 'bg-slate-100 text-slate-500' : good ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700';
  const Icon = flat ? Minus : up ? TrendingUp : TrendingDown;
  return <span className="inline-flex items-center gap-1 text-[11px]" data-testid={testId}><span className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-px font-semibold ${cls}`}><Icon className="h-3 w-3" />{pct == null ? '—' : abs != null ? `${abs > 0 ? '+' : ''}${abs}` : `${up ? '+' : pct < 0 ? '-' : ''}${Math.abs(pct)}${suffix}`}</span>{label && <span className="text-slate-400">{label}</span>}</span>;
};

/** KPI card: icon tile, count-up value (numeric) or `display`, label, optional delta/prev/sparkline. Values are passed in — no math here. */
export const AnKpiCard = ({ icon: Icon, tone = 'blue', label, value, display, delta, prev, series, info, onClick, loading, testid, delay = 0, emphasis = false, compact = false }) => {
  const t = TONES[tone] || TONES.blue;
  const n = useCountUp(typeof value === 'number' ? value : null);
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag type={onClick ? 'button' : undefined} onClick={onClick} data-testid={testid} style={{ animationDelay: `${delay}ms` }}
      className={`an-rise an-hover relative text-left w-full overflow-hidden bg-gradient-to-br ${t.bg} via-white to-white rounded-2xl border border-slate-200/80 shadow-[0_1px_2px_rgba(15,23,42,.04),0_8px_24px_-14px_rgba(15,23,42,.14)] ${compact ? 'p-3 flex items-center gap-3' : 'p-4'} ${onClick ? 'cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300' : ''}`}>
      {compact ? (
        <>
          <div className={`h-9 w-9 rounded-xl flex items-center justify-center shrink-0 ${t.tile}`}><Icon className="h-4 w-4" /></div>
          <div className="min-w-0 flex-1">
            <div className={`text-[18px] font-bold leading-tight tabular-nums ${emphasis ? 'text-rose-600' : 'text-slate-900'}`} data-testid={testid ? `${testid}-value` : undefined}>{loading ? <span className="inline-block h-5 w-8 rounded bg-slate-100 animate-pulse" /> : (display ?? (n ?? 0).toLocaleString('en-US'))}</div>
            <div className="text-[11.5px] text-slate-500 leading-tight">{label}</div>
          </div>
        </>
      ) : (
        <>
          <div className="flex items-start justify-between gap-2">
            <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${t.tile}`}><Icon className="h-5 w-5" /></div>
            {delta}
          </div>
          <div className="flex items-end justify-between gap-2 mt-2">
            <div className="min-w-0">
              <div className={`text-[24px] font-bold leading-tight tabular-nums ${emphasis ? 'text-rose-600' : 'text-slate-900'}`} data-testid={testid ? `${testid}-value` : undefined}>{loading ? <span className="inline-block h-6 w-10 rounded bg-slate-100 animate-pulse" /> : (display ?? (n ?? 0).toLocaleString('en-US'))}</div>
              <div className="flex items-center gap-1 text-[12.5px] text-slate-500 truncate">{label}{info}</div>
              {prev !== undefined && <div className="text-[11px] text-slate-400 mt-0.5">{prev}</div>}
            </div>
            {series && series.length > 1 && <div className="shrink-0 hidden md:block"><Sparkline data={series} color={t.line} w={84} h={34} testid="an-sparkline" /></div>}
          </div>
        </>
      )}
    </Tag>
  );
};

/** Chart/section container. */
export const AnChartCard = ({ title, subtitle, action, children, testId, className = '', delay = 0 }) => (
  <AnCard className={`p-5 ${className}`} testId={testId} delay={delay}>
    <div className="flex items-start justify-between mb-4 gap-3">
      <div className="min-w-0"><h3 className="text-sm font-semibold text-slate-900">{title}</h3>{subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}</div>
      {action}
    </div>
    {children}
  </AnCard>
);

export const AnSkeleton = ({ rows = 3, className = '' }) => (
  <div className={`space-y-2 ${className}`} aria-hidden="true" data-testid="an-skeleton">
    {Array.from({ length: rows }).map((_, i) => <div key={i} className="h-4 rounded bg-slate-100 animate-pulse" style={{ width: `${90 - i * 12}%` }} />)}
  </div>
);
